import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import {
  TreeStructure, Key, Copy, DownloadSimple, Play, ArrowRight,
  ShieldCheck, Broadcast, CheckCircle,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

export default function EdgeAgent() {
  const [key, setKey] = useState("");
  const [reveal, setReveal] = useState(false);
  const [script, setScript] = useState("");
  const [edgeCams, setEdgeCams] = useState([]);
  const [running, setRunning] = useState(false);
  const navigate = useNavigate();
  const ingestUrl = `${process.env.REACT_APP_BACKEND_URL}/api/agent`;

  const loadCams = () => api.get("/cameras").then((r) => setEdgeCams(r.data.filter((c) => c.source === "edge")));

  useEffect(() => {
    api.get("/agent/token").then((r) => setKey(r.data.agent_key)).catch(() => {});
    fetch(`${API}/agent/script`).then((r) => r.text()).then(setScript).catch(() => {});
    loadCams();
  }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const download = () => {
    const url = URL.createObjectURL(new Blob([script], { type: "text/x-python" }));
    const a = document.createElement("a");
    a.href = url; a.download = "sentryvision_agent.py"; a.click();
    URL.revokeObjectURL(url);
  };

  const runDemo = async () => {
    setRunning(true);
    try {
      await api.post("/agent/demo");
      toast.success("Edge camera connected & frame received", { description: "Open Live Watch to see it analyzed." });
      loadCams();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Demo failed");
    } finally {
      setRunning(false);
    }
  };

  const Step = ({ n, title, children }) => (
    <div className="flex gap-3">
      <div className="shrink-0 w-6 h-6 bg-[#007AFF]/15 border border-[#007AFF]/40 text-[#3395FF] font-mono-data text-xs flex items-center justify-center">{n}</div>
      <div className="flex-1"><p className="text-sm font-medium">{title}</p><div className="text-xs text-[#A1A1AA] mt-1 leading-relaxed">{children}</div></div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      <div className="xl:col-span-7 space-y-4">
        <div className="bg-[#121214] border border-white/10 p-4">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-1 flex items-center gap-2">
            <TreeStructure size={16} weight="bold" className="text-[#007AFF]" /> How the Edge Agent Works
          </h2>
          <p className="text-xs text-[#A1A1AA] leading-relaxed mb-4">
            Your store cameras live on a private network the cloud can't reach. The Edge Agent is a tiny program you run on any always-on PC in the store. It grabs a frame from each camera locally and relays it up to SentryVision — so cameras stay off the public internet and everything analyzes here automatically in Live Watch.
          </p>
          <div className="space-y-4">
            <Step n="1" title="Install Python + the agent">Put <span className="font-mono-data text-[#3395FF]">sentryvision_agent.py</span> on a store PC and run <span className="font-mono-data">pip install requests opencv-python onvif-zeep</span> (no ffmpeg needed — opencv reads RTSP, onvif-zeep enables PTZ auto-patrol).</Step>
            <Step n="2" title="Paste your key & cameras">Open the script, set your Agent Key + dashboard URL (both below), and paste each camera's RTSP URL.</Step>
            <Step n="3" title="Run it">Run <span className="font-mono-data">python sentryvision_agent.py</span>. It registers your cameras and streams frames every few seconds.</Step>
            <Step n="4" title="Monitor, sweep or follow motion">Cameras appear as LIVE edge feeds in Live Watch. For pan/tilt cameras, open the <span className="text-[#3395FF]">Patrol page</span> and pick a mode: <span className="text-[#3395FF]">Sweep</span> to scan the room like a fan, or <span className="text-[#3395FF]">Follow Motion</span> to auto-track whoever is moving.</Step>
          </div>
        </div>

        <div className="bg-[#121214] border border-white/10 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-black uppercase tracking-tighter text-xs text-[#A1A1AA]">Agent Script</h3>
            <div className="flex gap-2">
              <button data-testid="download-agent" onClick={download} className="flex items-center gap-1.5 bg-[#007AFF] hover:bg-[#3395FF] text-white px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wide transition-colors"><DownloadSimple size={14} weight="bold" /> Download .py</button>
              <button onClick={() => copy(script, "Script")} className="flex items-center gap-1.5 bg-[#1A1A1E] border border-white/10 hover:border-white/30 text-white px-3 py-1.5 text-xs transition-colors"><Copy size={14} /> Copy</button>
            </div>
          </div>
          <pre className="bg-black/60 border border-white/10 p-3 text-[11px] font-mono-data text-[#A1A1AA] overflow-x-auto max-h-64 overflow-y-auto leading-relaxed">{script || "Loading…"}</pre>
        </div>
      </div>

      <div className="xl:col-span-5 space-y-4">
        <div className="bg-[#121214] border border-white/10 p-4">
          <h3 className="font-display font-black uppercase tracking-tighter text-xs text-[#A1A1AA] mb-3 flex items-center gap-2"><Key size={14} weight="bold" /> Your Credentials</h3>
          <label className="block text-[10px] uppercase tracking-widest text-[#71717A] mb-1">Ingest URL</label>
          <div className="flex items-center gap-2 mb-3">
            <code className="flex-1 bg-[#1A1A1E] border border-white/10 px-2 py-2 text-[11px] font-mono-data text-[#A1A1AA] truncate">{ingestUrl}</code>
            <button onClick={() => copy(ingestUrl, "URL")} className="text-[#71717A] hover:text-white transition-colors"><Copy size={16} /></button>
          </div>
          <label className="block text-[10px] uppercase tracking-widest text-[#71717A] mb-1">Agent Key</label>
          <div className="flex items-center gap-2">
            <code data-testid="agent-key" className="flex-1 bg-[#1A1A1E] border border-white/10 px-2 py-2 text-[11px] font-mono-data text-[#A1A1AA] truncate">{reveal ? key : "•".repeat(Math.min(key.length, 32))}</code>
            <button onClick={() => setReveal(!reveal)} className="text-[10px] font-mono-data text-[#3395FF] hover:text-white transition-colors uppercase">{reveal ? "Hide" : "Show"}</button>
            <button onClick={() => copy(key, "Agent key")} className="text-[#71717A] hover:text-white transition-colors"><Copy size={16} /></button>
          </div>
          <p className="text-[10px] text-[#71717A] mt-3 flex items-center gap-1"><ShieldCheck size={12} /> Keep this secret — it authorizes frame uploads.</p>
        </div>

        <div className="bg-[#007AFF]/10 border border-[#007AFF]/40 p-4">
          <h3 className="font-display font-black uppercase tracking-tighter text-xs text-white mb-1 flex items-center gap-2"><Play size={14} weight="fill" /> Try It Without Hardware</h3>
          <p className="text-xs text-[#E4E4E7] leading-relaxed mb-3">Run a simulated edge camera: it registers a feed and pushes a real frame through the exact same pipeline your agent uses.</p>
          <button data-testid="run-demo" onClick={runDemo} disabled={running}
            className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white py-2.5 font-display font-bold uppercase tracking-wide text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {running ? "Connecting…" : <><Broadcast size={16} weight="bold" /> Simulate Edge Camera</>}
          </button>
        </div>

        <div className="bg-[#121214] border border-white/10 p-4">
          <h3 className="font-display font-black uppercase tracking-tighter text-xs text-[#A1A1AA] mb-3">Connected Edge Cameras</h3>
          {edgeCams.length === 0 && <p className="text-xs text-[#71717A] font-mono-data">No edge cameras yet. Run the demo or start your agent.</p>}
          <div className="space-y-2">
            {edgeCams.map((c) => (
              <div key={c.id} data-testid={`edge-cam-${c.id}`} className="flex items-center justify-between border border-white/10 p-2.5">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-[#32D74B]" />
                  <div>
                    <p className="text-sm font-medium leading-none">{c.name}</p>
                    <p className="font-mono-data text-[10px] text-[#71717A] mt-1">{c.zone} · {c.ai}{c.last_frame_at ? ` · frame ${new Date(c.last_frame_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}</p>
                  </div>
                </div>
                <span className="font-mono-data text-[10px] px-1.5 py-0.5 bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40">LIVE</span>
              </div>
            ))}
          </div>
          {edgeCams.length > 0 && (
            <button onClick={() => navigate("/watch")} className="w-full mt-3 flex items-center justify-center gap-1.5 bg-[#1A1A1E] border border-white/10 hover:border-[#007AFF] text-white py-2 text-xs font-display font-bold uppercase tracking-wide transition-colors">
              Open Live Watch <ArrowRight size={14} weight="bold" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
